import { FunctionComponent } from "react";

import { Link } from "react-router-dom";

import {
	faGear,
	faUserMagnifyingGlass,
	faBuilding,
	faChartLine,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";
import { CustomEventType } from "@/enum/custom-event.enum";
import { ScrapeLevel } from "@/enum/job.enum";
import { ListSourceType } from "@/enum/list.enum";

import { ListCampaignsDropdown } from "./ListCampaignsDropdown";
import { ListSourcesDropdown } from "./ListSourcesDropdown";

interface ListActionLinksProps {
	id: string;
	sourceType?: ListSourceType;
	specialType?: string | null;
	entityType?: CsvEnrichmentType | CustomEventType | null;
	scrapeLevel?: ScrapeLevel | null;
	isSequencerActive?: boolean;
	/** With no campaign yet the control is a "Create a campaign" CTA, else the Campaigns dropdown. */
	hasCampaigns?: boolean;
	variant?: "default" | "compact";
}

const defaultLinkClassName =
	"flex items-center gap-1.5 text-sm text-text-meta hover:text-gray-900 border border-border-light rounded-lg px-2 py-1 hover:border-blue-action";

const compactLinkClassName =
	"flex items-center gap-1 text-xs font-medium text-text-meta hover:text-text border border-border-light rounded-md px-2 h-8 hover:border-blue-action whitespace-nowrap";

export const ListActionLinks: FunctionComponent<ListActionLinksProps> = ({
	id,
	sourceType,
	specialType,
	entityType,
	scrapeLevel,
	isSequencerActive,
	hasCampaigns = true,
	variant = "default",
}) => {
	const linkClassName = variant === "compact" ? compactLinkClassName : defaultLinkClassName;
	const iconClassName =
		variant === "compact" ? "w-3 h-3 text-text-meta" : "w-3.5 h-3.5 text-text-meta";
	const gearClassName = variant === "compact" ? "w-3 h-3 text-text-meta" : "w-3 h-3 text-text-meta";
	const wrapperClass =
		variant === "compact" ? "flex items-center gap-1" : "flex items-center gap-2";

	return (
		<div className={wrapperClass}>
			{specialType === null &&
				sourceType !== ListSourceType.EMPTY &&
				sourceType !== ListSourceType.LI_CONNECTIONS && (
					<ListSourcesDropdown
						iconClassName={iconClassName}
						listId={Number(id)}
						triggerClassName={linkClassName}
					/>
				)}

			{(entityType === CsvEnrichmentType.ORGANIZATION ||
				scrapeLevel === ScrapeLevel.GET_ENRICHMENT) && (
				<>
					<Link className={linkClassName} to={`/app/lists/create/details/${id}`}>
						<Icon className={iconClassName} icon={faUserMagnifyingGlass} />
						<span>Find Person</span>
					</Link>
					{sourceType !== ListSourceType.PARALLEL_AI ? (
						<Link className={linkClassName} to={`/app/lists/create/filter/${id}`}>
							<Icon className={iconClassName} icon={faBuilding} />
							<span>Company Qualification</span>
						</Link>
					) : (
						""
					)}
				</>
			)}

			{isSequencerActive && (
				<ListCampaignsDropdown
					hasCampaigns={hasCampaigns}
					iconClassName={iconClassName}
					listId={Number(id)}
					triggerClassName={linkClassName}
				/>
			)}

			<Link className={linkClassName} to={`/app/analytics?listId=${id}`}>
				<Icon className={iconClassName} icon={faChartLine} />
				<span>Analytics</span>
			</Link>

			<Link className={linkClassName} to={`/app/lists/create/settings/${id}`}>
				<Icon className={gearClassName} icon={faGear} />
				<span>List Settings</span>
			</Link>
		</div>
	);
};
